import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Dashboard from '../dashboard';
import Files from './files';
import Actions from './actions';
import { importUrl as importFromUrl } from '../import/importSlice';
import { useQuery, useUpdateQuery } from '../../hooks/urlHooks';
import { updateMarketingIntentThunk } from '../plan/planSlice';
import { URLParamsType } from '../../types';
import useLimits from '../plan/hooks/limits';
import useUserTags from '../../hooks/useTags';
import TeamDeletionCover from '../team/TeamDeletingCover';
import { RootState } from '../../reducers';
import MultiFileChat from '../chat/multi-file';
import { pushModal } from '../globalModals/globalModalsSlice';
import UploadDropZone from './UploadDropZone';
import { USER_PACKAGE } from '../plan/userPackage';
import SocialMediaImportDialog from '../../components/SocialMediaImportDialog';
import ImportLoadingModal from '../../components/ImportLoadingModal';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { SelectModeProvider } from './context/SelectModeContext';

// const ONBOARDING_STEP_TAG_VALUE = '2024-Dec';

export const Library = () => {
  const { t } = useTranslation();
  const { teamId, folderId } = useParams<URLParamsType>();
  const { importUrl: importUrlFromQuery, intent, importFlow } = useQuery();
  const updateQuery = useUpdateQuery();
  const dispatch = useDispatch();
  const guardLimit = useLimits();
  // const { getTag } = useUserTags();
  useUserTags();
  // const { getFeature } = useBooleanFeature();
  useBooleanFeature();
  const { team } = useSelector((state: RootState) => state);
  const teamDeleteRequested = !!team.selectedTeam?.deleteRequestedAt;
  // const { files } = useSelector((state: RootState) => state.library);
  const { subscription } = useSelector((state: RootState) => state.plan);
  const isFreeUser = subscription?.package === USER_PACKAGE.FREE_LOGGEDIN;
  const selectedFolder = useSelector(
    (state: RootState) => state.library.selectedFolder,
  );
  const [templateModalOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [showImportLoading, setShowImportLoading] = useState(false);

  // Track if we're coming from the video view (for slide animation)
  // Check sessionStorage flag that was set when navigating to video
  const cameFromVideo = useRef(false);

  useEffect(() => {
    // Check if we navigated back from video view
    const navigatedToVideo = sessionStorage.getItem('navigatedToVideo');
    if (navigatedToVideo === 'true') {
      cameFromVideo.current = true;
      // Clear the flag so it doesn't affect subsequent navigations
      sessionStorage.removeItem('navigatedToVideo');
    }
  }, []);

  // Helper function to check if a URL is Instagram
  const isInstagramUrl = (url: string): boolean => {
    const lowercaseUrl = url.toLowerCase();
    return (
      lowercaseUrl.includes('instagram.com/p/') ||
      lowercaseUrl.includes('instagram.com/reel/') ||
      lowercaseUrl.includes('instagram.com/tv/')
    );
  };

  // Helper function to check if a URL is from YouTube (premium import)
  const isPremiumImportUrl = (url: string): boolean => {
    const lowercaseUrl = url.toLowerCase();
    return (
      // YouTube URLs only
      lowercaseUrl.includes('youtube') || lowercaseUrl.includes('youtu.be')
    );
  };

  // Update page title
  useEffect(() => {
    document.title = `${
      selectedFolder?.name || t('library.title')
    } - ScreenApp`;
    return () => {
      document.title = 'ScreenApp';
    };
  }, [selectedFolder?.name, t]);

  // Check for archived recordings when selected team changes
  useEffect(() => {
    if (team.selectedTeam?.archivedAt) {
      dispatch(
        setAlertMessage({
          type: 'info',
          message: t('library.alerts.archiveRecovery'),
        }),
      );
    }
  }, [team.selectedTeam, t]);

  // Capture and save marketing intent from URL params
  useEffect(() => {
    if (intent) {
      // Save intent to localStorage and database
      dispatch(updateMarketingIntentThunk(intent) as any);
    }
  }, [intent, dispatch]);

  // Handle actions based on query params
  useEffect(() => {
    const handleImport = async () => {
      if (importUrlFromQuery && teamId && folderId) {
        setShowImportLoading(true);
        try {
          // Check if it's an Instagram URL - open ImportModal with auto-filled URL
          if (isInstagramUrl(importUrlFromQuery)) {
            dispatch(
              pushModal({
                type: 'import-urls-modal',
                url: importUrlFromQuery,
              }),
            );
            setShowImportLoading(false);
            updateQuery({ importUrl: undefined, intent: undefined });
            return;
          }

          // Only show upgrade dialog for free users attempting to import premium content (YouTube)
          if (isFreeUser && isPremiumImportUrl(importUrlFromQuery)) {
            // Show YouTube upgrade dialog
            setMediaUrl(importUrlFromQuery);
            setImportDialogOpen(true);
          } else {
            // Import from non-premium URL or paid user URL
            await dispatch(
              importFromUrl({
                teamId,
                folderId,
                url: importUrlFromQuery,
                intent,
                useImportV2: importFlow === 'v2',
              }),
            );
          }
        } catch (error) {
          // Error is already handled in the thunk
          console.error('Import failed:', error);
        } finally {
          setShowImportLoading(false);
          // Clear the import URL from query params immediately after starting the import
          updateQuery({ importUrl: undefined, intent: undefined });
        }
      }
    };

    handleImport();
  }, [importUrlFromQuery]);

  useEffect(() => {
    guardLimit('login', () => {});
  }, []);

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setMediaUrl('');
  };

  if (teamDeleteRequested) {
    return (
      <Dashboard>
        <TeamDeletionCover />
      </Dashboard>
    );
  }

  // Only animate if coming back from video view
  const shouldAnimate = cameFromVideo.current;

  return (
    <Dashboard>
      <motion.div
        initial={shouldAnimate ? { opacity: 0, x: '-5%' } : false}
        animate={{ opacity: 1, x: 0 }}
        transition={
          shouldAnimate
            ? {
                duration: 0.35,
                ease: [0.32, 0.72, 0, 1], // iOS-style ease curve
              }
            : { duration: 0 }
        }
      >
        <SelectModeProvider>
          <UploadDropZone>
            <div>
              <Actions />
              {templateModalOpen && <MultiFileChat open={templateModalOpen} />}
            </div>
            <div>
              <Files />
            </div>
          </UploadDropZone>

          {/* YouTube Import Dialog */}
          <SocialMediaImportDialog
            open={importDialogOpen}
            onClose={handleCloseImportDialog}
            url={mediaUrl}
          />

          {/* Import Loading Modal */}
          <ImportLoadingModal open={showImportLoading} />
        </SelectModeProvider>
      </motion.div>
    </Dashboard>
  );
};

export default Library;
